import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  CalendarDays,
  FileDown,
  Film,
  HandCoins,
  ImagePlus,
  Loader2,
  Lock,
  LogOut,
  Pencil,
  Plus,
  ShieldCheck,
  Trash2,
  Users,
} from "lucide-react";
import { api, memberCardUrl } from "@/lib/api";

const TOKEN_KEY = "suvct_admin_token";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const STATUS_LABEL = {
  paid: { text: "ચુકવેલું", cls: "bg-leaf-soft text-leaf-dark" },
  pledged: { text: "બાંયધરી", cls: "bg-saffron-soft text-saffron-dark" },
  created: { text: "અધૂરું", cls: "bg-stone-100 text-ink/60" },
  failed: { text: "નિષ્ફળ", cls: "bg-terracotta-soft text-terracotta" },
};

const EMPTY_EVENT = { badge: "", title: "", desc: "", place: "", active: true, photo: "" };

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("donations");
  const [donations, setDonations] = useState([]);
  const [members, setMembers] = useState([]);
  const [events, setEvents] = useState([]);
  const [media, setMedia] = useState([]);
  const [stats, setStats] = useState(null);
  const [eventForm, setEventForm] = useState(null);
  const [eventSaving, setEventSaving] = useState(false);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaCaption, setMediaCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async (t) => {
    try {
      const [d, m, s, ev, md] = await Promise.all([
        api.adminDonations(t),
        api.adminMembers(t),
        api.getStats(),
        api.adminEvents(t),
        api.getMedia(),
      ]);
      setDonations(d);
      setMembers(m);
      setStats(s);
      setEvents(ev);
      setMedia(md);
    } catch (e) {
      if (e.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken(null);
      }
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await api.adminLogin(password);
      sessionStorage.setItem(TOKEN_KEY, res.token);
      setToken(res.token);
    } catch (err) {
      const code = err.response?.data?.detail;
      setError(
        code === "locked"
          ? "ઘણા પ્રયાસો — ૧૫ મિનિટ પછી ફરી પ્રયત્ન કરો"
          : "ખોટો પાસવર્ડ — ફરી પ્રયત્ન કરો"
      );
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setPassword("");
  };

  const downloadCsv = async (kind) => {
    try {
      const blob = await api.exportCsv(token, kind);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `suvct-${kind}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("ડાઉનલોડ નિષ્ફળ — ફરી પ્રયત્ન કરો");
    }
  };

  const saveEvent = async (e) => {
    e.preventDefault();
    setEventSaving(true);
    try {
      if (eventForm.id) {
        await api.adminUpdateEvent(token, eventForm.id, eventForm);
        toast.success("ઇવેન્ટ અપડેટ થયો");
      } else {
        await api.adminCreateEvent(token, eventForm);
        toast.success("નવો ઇવેન્ટ ઉમેરાયો");
      }
      setEventForm(null);
      load(token);
    } catch {
      toast.error("સાચવવામાં ભૂલ — ફરી પ્રયત્ન કરો");
    } finally {
      setEventSaving(false);
    }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm("આ ઇવેન્ટ કાઢી નાખવો છે?")) return;
    try {
      await api.adminDeleteEvent(token, id);
      toast.success("ઇવેન્ટ કાઢી નાખાયો");
      load(token);
    } catch {
      toast.error("કાઢી નાખવામાં ભૂલ");
    }
  };

  const uploadMedia = async (e) => {
    e.preventDefault();
    if (!mediaFile) {
      toast.error("પહેલા ફોટો કે વિડિયો પસંદ કરો");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", mediaFile);
      fd.append("caption", mediaCaption);
      await api.adminUploadMedia(token, fd);
      toast.success("અપલોડ થઈ ગયું — ગેલેરીમાં દેખાશે");
      setMediaFile(null);
      setMediaCaption("");
      if (fileRef.current) fileRef.current.value = "";
      load(token);
    } catch (err) {
      const code = err.response?.data?.detail;
      toast.error(
        code === "file_too_large"
          ? "ફાઇલ 50MB થી નાની હોવી જોઈએ"
          : code === "only_photos_and_videos"
          ? "ફક્ત ફોટા (JPG/PNG) કે વિડિયો (MP4)"
          : "અપલોડ નિષ્ફળ — ફરી પ્રયત્ન કરો"
      );
    } finally {
      setUploading(false);
    }
  };

  const deleteMedia = async (id) => {
    if (!window.confirm("આ ફોટો/વિડિયો કાઢી નાખવો છે?")) return;
    try {
      await api.adminDeleteMedia(token, id);
      toast.success("કાઢી નાખાયું");
      load(token);
    } catch {
      toast.error("કાઢી નાખવામાં ભૂલ");
    }
  };

  if (!token) {
    return (
      <div data-testid="admin-login-page" className="min-h-screen bg-cream flex items-center justify-center px-5">
        <motion.form
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-sm rounded-3xl border border-stone-200 bg-white p-8 shadow-[0_24px_60px_rgb(31,41,55,0.1)]"
        >
          <div className="flex flex-col items-center mb-7">
            <img src="/logo.jpeg" alt="ટ્રસ્ટ લોગો" className="w-16 h-16 rounded-full object-cover ring-2 ring-leaf/30 mb-4" />
            <h1 className="font-display text-2xl text-ink">એડમિન લોગિન</h1>
            <p className="font-head text-sm text-ink/55 mt-1">માત્ર ટ્રસ્ટીઓ માટે</p>
          </div>
          <label className="font-head font-semibold text-sm text-ink/70 block mb-1.5">
            પાસવર્ડ
          </label>
          <input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="w-full rounded-xl border border-stone-300 bg-cream px-4 py-3 font-body text-ink outline-none focus:border-leaf focus:ring-2 focus:ring-leaf/20 transition-colors mb-4"
          />
          {error && (
            <p data-testid="admin-login-error" className="font-head text-sm text-terracotta font-semibold mb-4">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 rounded-full bg-leaf px-6 py-3.5 font-head font-bold text-cream hover:bg-leaf-dark transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Lock className="w-4 h-4" />}
            પ્રવેશ કરો
          </button>
          <a
            data-testid="admin-back-home-link"
            href="/"
            className="block text-center mt-5 font-head text-sm text-ink/50 hover:text-saffron transition-colors"
          >
            ← મુખ્ય પૃષ્ઠ પર પાછા જાઓ
          </a>
        </motion.form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-cream">
      <header className="bg-ink text-cream">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/logo.jpeg" alt="ટ્રસ્ટ લોગો" className="w-10 h-10 rounded-full object-cover ring-2 ring-saffron/40" />
            <div>
              <p className="font-head font-bold leading-tight">એડમિન ડેશબોર્ડ</p>
              <p className="font-head text-xs text-cream/60">શ્રી યુનિક વિકલાંગ ચેરિટેબલ ટ્રસ્ટ</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a data-testid="admin-view-site-link" href="/" className="hidden sm:block font-head text-sm text-cream/70 hover:text-saffron transition-colors">
              સાઇટ જુઓ
            </a>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-4 py-2 font-head font-semibold text-sm hover:bg-terracotta transition-colors"
            >
              <LogOut className="w-4 h-4" />
              લોગઆઉટ
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {[
              { icon: HandCoins, label: "ચુકવેલ દાન", value: `₹${stats.total_paid.toLocaleString("en-IN")}`, testid: "admin-stat-paid" },
              { icon: ShieldCheck, label: "બાંયધરી દાન", value: `₹${stats.total_pledged.toLocaleString("en-IN")}`, testid: "admin-stat-pledged" },
              { icon: HandCoins, label: "કુલ દાતા", value: stats.donor_count, testid: "admin-stat-donors" },
              { icon: Users, label: "કુલ સભ્યો", value: stats.member_count, testid: "admin-stat-members" },
            ].map((s) => (
              <div
                key={s.testid}
                data-testid={s.testid}
                className="rounded-2xl border border-stone-200 bg-white p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
              >
                <s.icon className="w-5 h-5 text-saffron mb-3" />
                <p className="font-display text-2xl text-ink">{s.value}</p>
                <p className="font-head text-sm text-ink/55 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        <div data-testid="admin-tabs" className="flex flex-wrap gap-2 rounded-full bg-stone-100 p-1.5 mb-6 w-fit">
          {[
            { id: "donations", label: `દાન (${donations.length})` },
            { id: "members", label: `સભ્યો (${members.length})` },
            { id: "events", label: `ઇવેન્ટ્સ (${events.length})` },
            { id: "media", label: `ફોટા/વિડિયો (${media.length})` },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`rounded-full px-5 py-2.5 font-head font-bold text-sm transition-all duration-200 ${
                tab === t.id ? "bg-terracotta text-cream shadow-md" : "text-ink/60 hover:text-ink"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "donations" && (
          <div data-testid="admin-donations-panel">
            <div className="flex justify-end mb-4">
              <button
                data-testid="admin-export-donations"
                onClick={() => downloadCsv("donations")}
                className="flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 font-head font-bold text-sm text-cream hover:bg-ink/80 transition-colors"
              >
                <FileDown className="w-4 h-4" />
                CSV ડાઉનલોડ
              </button>
            </div>
            <div data-testid="admin-donations-table" className="rounded-2xl border border-stone-200 bg-white overflow-x-auto shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <table className="w-full min-w-[640px] text-left">
                <thead>
                  <tr className="border-b border-stone-200 font-head text-xs text-ink/50">
                    <th className="px-5 py-4">નામ</th>
                    <th className="px-5 py-4">રકમ</th>
                    <th className="px-5 py-4">રીત</th>
                    <th className="px-5 py-4">સ્થિતિ</th>
                    <th className="px-5 py-4">તારીખ</th>
                  </tr>
                </thead>
                <tbody>
                  {donations.length === 0 && (
                    <tr><td colSpan={5} className="px-5 py-10 text-center font-head text-ink/50">હજુ કોઈ દાન નથી</td></tr>
                  )}
                  {donations.map((d) => (
                    <tr key={d.id} className="border-b border-stone-100 last:border-0 font-body text-sm">
                      <td className="px-5 py-3.5 font-head font-semibold text-ink">{d.name}{d.phone ? <span className="block text-xs text-ink/50 font-normal">{d.phone}</span> : null}</td>
                      <td className="px-5 py-3.5 font-bold text-ink">₹{d.amount.toLocaleString("en-IN")}</td>
                      <td className="px-5 py-3.5 text-ink/70">{d.method === "online" ? "ઓનલાઈન" : d.method === "upi" ? "UPI" : "બેંક"}{d.sms_status === "sent" ? <span className="block text-xs text-leaf-dark font-head font-bold">SMS મોકલાઈ</span> : null}{d.whatsapp_status === "sent" ? <span className="block text-xs text-leaf-dark font-head font-bold">WhatsApp મોકલાઈ</span> : null}</td>
                      <td className="px-5 py-3.5">
                        <span className={`rounded-full px-3 py-1 font-head text-xs font-bold ${STATUS_LABEL[d.status]?.cls || "bg-stone-100 text-ink/60"}`}>
                          {STATUS_LABEL[d.status]?.text || d.status}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-ink/60">{fmtDate(d.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "members" && (
          <div data-testid="admin-members-panel">
            <div className="flex justify-end mb-4">
              <button
                data-testid="admin-export-members"
                onClick={() => downloadCsv("members")}
                className="flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 font-head font-bold text-sm text-cream hover:bg-ink/80 transition-colors"
              >
                <FileDown className="w-4 h-4" />
                CSV ડાઉનલોડ
              </button>
            </div>
            <div data-testid="admin-members-table" className="rounded-2xl border border-stone-200 bg-white overflow-x-auto shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <table className="w-full min-w-[640px] text-left">
                <thead>
                  <tr className="border-b border-stone-200 font-head text-xs text-ink/50">
                    <th className="px-5 py-4">નામ</th>
                    <th className="px-5 py-4">ફોન</th>
                    <th className="px-5 py-4">શહેર</th>
                    <th className="px-5 py-4">પ્રકાર</th>
                    <th className="px-5 py-4">તારીખ</th>
                    <th className="px-5 py-4">કાર્ડ</th>
                  </tr>
                </thead>
                <tbody>
                  {members.length === 0 && (
                    <tr><td colSpan={5} className="px-5 py-10 text-center font-head text-ink/50">હજુ કોઈ સભ્ય નથી</td></tr>
                  )}
                  {members.map((m) => (
                    <tr key={m.id} className="border-b border-stone-100 last:border-0 font-body text-sm">
                      <td className="px-5 py-3.5 font-head font-semibold text-ink">{m.name}</td>
                      <td className="px-5 py-3.5 text-ink/70">{m.phone}</td>
                      <td className="px-5 py-3.5 text-ink/70">{m.city || "—"}</td>
                      <td className="px-5 py-3.5">
                        <span className={`rounded-full px-3 py-1 font-head text-xs font-bold ${m.role === "volunteer" ? "bg-leaf-soft text-leaf-dark" : "bg-saffron-soft text-saffron-dark"}`}>
                          {m.role === "volunteer" ? "સ્વયંસેવક" : "સભ્ય"}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-ink/60">{fmtDate(m.created_at)}</td>
                      <td className="px-5 py-3.5">
                        <a
                          data-testid={`admin-member-card-${m.id}`}
                          href={memberCardUrl(m.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="મેમ્બર કાર્ડ ડાઉનલોડ"
                          className="inline-flex w-9 h-9 rounded-full bg-leaf-soft text-leaf-dark items-center justify-center hover:bg-leaf hover:text-cream transition-colors"
                        >
                          <FileDown className="w-4 h-4" />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "events" && (
          <div data-testid="admin-events-panel">
            <button
              data-testid="admin-event-add-button"
              onClick={() => setEventForm({ ...EMPTY_EVENT })}
              className="mb-5 flex items-center gap-2 rounded-full bg-leaf px-5 py-2.5 font-head font-bold text-sm text-cream hover:bg-leaf-dark transition-colors"
            >
              <Plus className="w-4 h-4" />
              નવો ઇવેન્ટ ઉમેરો
            </button>

            {eventForm && (
              <form
                data-testid="admin-event-form"
                onSubmit={saveEvent}
                className="mb-6 rounded-2xl border border-stone-200 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] grid sm:grid-cols-2 gap-4"
              >
                <div>
                  <label className="font-head font-semibold text-sm text-ink/70 block mb-1.5">બેજ (દા.તા. દર ગુરુવારે) *</label>
                  <input
                    data-testid="admin-event-badge-input"
                    value={eventForm.badge}
                    onChange={(e) => setEventForm({ ...eventForm, badge: e.target.value })}
                    required
                    className="w-full rounded-xl border border-stone-300 bg-cream px-4 py-2.5 font-body text-ink outline-none focus:border-leaf focus:ring-2 focus:ring-leaf/20 transition-colors"
                  />
                </div>
                <div>
                  <label className="font-head font-semibold text-sm text-ink/70 block mb-1.5">શીર્ષક *</label>
                  <input
                    data-testid="admin-event-title-input"
                    value={eventForm.title}
                    onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                    required
                    className="w-full rounded-xl border border-stone-300 bg-cream px-4 py-2.5 font-body text-ink outline-none focus:border-leaf focus:ring-2 focus:ring-leaf/20 transition-colors"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="font-head font-semibold text-sm text-ink/70 block mb-1.5">વર્ણન *</label>
                  <textarea
                    data-testid="admin-event-desc-input"
                    value={eventForm.desc}
                    onChange={(e) => setEventForm({ ...eventForm, desc: e.target.value })}
                    required
                    rows={2}
                    className="w-full rounded-xl border border-stone-300 bg-cream px-4 py-2.5 font-body text-ink outline-none focus:border-leaf focus:ring-2 focus:ring-leaf/20 transition-colors resize-none"
                  />
                </div>
                <div>
                  <label className="font-head font-semibold text-sm text-ink/70 block mb-1.5">સ્થળ *</label>
                  <input
                    data-testid="admin-event-place-input"
                    value={eventForm.place}
                    onChange={(e) => setEventForm({ ...eventForm, place: e.target.value })}
                    required
                    className="w-full rounded-xl border border-stone-300 bg-cream px-4 py-2.5 font-body text-ink outline-none focus:border-leaf focus:ring-2 focus:ring-leaf/20 transition-colors"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2.5 cursor-pointer select-none pb-2">
                    <input
                      data-testid="admin-event-active-checkbox"
                      type="checkbox"
                      checked={eventForm.active}
                      onChange={(e) => setEventForm({ ...eventForm, active: e.target.checked })}
                      className="w-4 h-4 accent-[#1E8449]"
                    />
                    <span className="font-head text-sm text-ink/70">સાઇટ પર બતાવો (સક્રિય)</span>
                  </label>
                </div>
                <div className="sm:col-span-2">
                  <label className="font-head font-semibold text-sm text-ink/70 block mb-2">
                    ફોટો (વૈકલ્પિક — "ફોટા/વિડિયો" ટેબમાં અપલોડ કરેલા ફોટામાંથી પસંદ કરો)
                  </label>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      data-testid="admin-event-photo-none"
                      onClick={() => setEventForm({ ...eventForm, photo: "" })}
                      className={`rounded-xl border-2 px-4 h-16 font-head text-xs font-bold transition-colors ${
                        !eventForm.photo
                          ? "border-leaf bg-leaf-soft text-leaf-dark"
                          : "border-stone-200 text-ink/50 hover:border-stone-300"
                      }`}
                    >
                      કોઈ ફોટો નહીં
                    </button>
                    {media
                      .filter((m) => m.kind === "photo")
                      .slice(0, 12)
                      .map((m) => (
                        <button
                          key={m.id}
                          type="button"
                          data-testid={`admin-event-photo-${m.id}`}
                          onClick={() => setEventForm({ ...eventForm, photo: m.id })}
                          aria-label={m.caption || "ફોટો પસંદ કરો"}
                          className={`w-16 h-16 rounded-xl overflow-hidden border-2 transition-all ${
                            eventForm.photo === m.id
                              ? "border-leaf ring-2 ring-leaf/30"
                              : "border-transparent hover:border-stone-300"
                          }`}
                        >
                          <img src={api.mediaFileUrl(m.id)} alt={m.caption || ""} className="w-full h-full object-cover" />
                        </button>
                      ))}
                    {media.filter((m) => m.kind === "photo").length === 0 && (
                      <span className="font-head text-xs text-ink/45">
                        કોઈ ફોટો નથી — પહેલા "ફોટા/વિડિયો" ટેબમાં અપલોડ કરો
                      </span>
                    )}
                  </div>
                </div>
                <div className="sm:col-span-2 flex gap-3">
                  <button
                    data-testid="admin-event-save-button"
                    type="submit"
                    disabled={eventSaving}
                    className="flex items-center gap-2 rounded-full bg-terracotta px-6 py-2.5 font-head font-bold text-sm text-cream hover:bg-terracotta/90 transition-colors disabled:opacity-60"
                  >
                    {eventSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarDays className="w-4 h-4" />}
                    સાચવો
                  </button>
                  <button
                    data-testid="admin-event-cancel-button"
                    type="button"
                    onClick={() => setEventForm(null)}
                    className="rounded-full border border-stone-300 px-6 py-2.5 font-head font-bold text-sm text-ink/60 hover:text-ink transition-colors"
                  >
                    રદ કરો
                  </button>
                </div>
              </form>
            )}

            <div className="grid gap-3">
              {events.length === 0 && (
                <p className="font-head text-ink/50 text-center py-10">હજુ કોઈ ઇવેન્ટ નથી</p>
              )}
              {events.map((ev) => (
                <div
                  key={ev.id}
                  data-testid={`admin-event-row-${ev.id}`}
                  className="flex items-start justify-between gap-4 rounded-2xl border border-stone-200 bg-white p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
                >
                  {ev.photo && (
                    <img
                      src={api.mediaFileUrl(ev.photo)}
                      alt=""
                      className="w-16 h-16 rounded-xl object-cover shrink-0"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1.5">
                      <span className="rounded-full bg-saffron-soft px-3 py-1 font-head text-xs font-bold text-saffron-dark">
                        {ev.badge}
                      </span>
                      {!ev.active && (
                        <span className="rounded-full bg-stone-100 px-3 py-1 font-head text-xs font-bold text-ink/50">
                          છુપાયેલો
                        </span>
                      )}
                    </div>
                    <p className="font-head font-bold text-ink">{ev.title}</p>
                    <p className="font-body text-sm text-ink/60 mt-0.5 line-clamp-2">{ev.desc}</p>
                    <p className="font-head text-xs text-leaf-dark font-semibold mt-1.5">{ev.place}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      data-testid={`admin-event-edit-${ev.id}`}
                      onClick={() => setEventForm({ ...EMPTY_EVENT, ...ev, photo: ev.photo || "" })}
                      aria-label="ફેરફાર કરો"
                      className="w-9 h-9 rounded-full bg-saffron-soft text-saffron-dark flex items-center justify-center hover:bg-saffron hover:text-cream transition-colors"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`admin-event-delete-${ev.id}`}
                      onClick={() => deleteEvent(ev.id)}
                      aria-label="કાઢી નાખો"
                      className="w-9 h-9 rounded-full bg-terracotta-soft text-terracotta flex items-center justify-center hover:bg-terracotta hover:text-cream transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "media" && (
          <div data-testid="admin-media-panel">
            <form
              data-testid="admin-media-upload-form"
              onSubmit={uploadMedia}
              className="mb-6 rounded-2xl border border-stone-200 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex flex-col sm:flex-row sm:items-end gap-4"
            >
              <div className="flex-1">
                <label className="font-head font-semibold text-sm text-ink/70 block mb-1.5">
                  ફોટો કે વિડિયો પસંદ કરો *
                </label>
                <input
                  ref={fileRef}
                  data-testid="admin-media-file-input"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
                  onChange={(e) => setMediaFile(e.target.files?.[0] || null)}
                  className="w-full rounded-xl border border-stone-300 bg-cream px-4 py-2 font-body text-sm text-ink file:mr-3 file:rounded-full file:border-0 file:bg-saffron-soft file:px-4 file:py-1.5 file:font-head file:text-xs file:font-bold file:text-saffron-dark"
                />
              </div>
              <div className="flex-1">
                <label className="font-head font-semibold text-sm text-ink/70 block mb-1.5">
                  કેપ્શન (વૈકલ્પિક)
                </label>
                <input
                  data-testid="admin-media-caption-input"
                  value={mediaCaption}
                  onChange={(e) => setMediaCaption(e.target.value)}
                  placeholder="દા.તા. મેડિકલ કેમ્પ — ઓક્ટોબર"
                  className="w-full rounded-xl border border-stone-300 bg-cream px-4 py-2.5 font-body text-ink outline-none focus:border-leaf focus:ring-2 focus:ring-leaf/20 transition-colors"
                />
              </div>
              <button
                data-testid="admin-media-upload-button"
                type="submit"
                disabled={uploading}
                className="flex items-center justify-center gap-2 rounded-full bg-leaf px-6 py-3 font-head font-bold text-sm text-cream hover:bg-leaf-dark transition-colors disabled:opacity-60 shrink-0"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
                અપલોડ કરો
              </button>
            </form>
            <p className="font-head text-xs text-ink/50 mb-5">
              JPG, PNG, WEBP, GIF કે MP4/WEBM — મહત્તમ 50MB. અપલોડ થતાં જ ગેલેરીના "નવા અપલોડ" વિભાગમાં દેખાશે.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {media.length === 0 && (
                <p className="col-span-full font-head text-ink/50 text-center py-10">
                  હજુ કોઈ ફોટો/વિડિયો અપલોડ થયો નથી
                </p>
              )}
              {media.map((m) => (
                <div
                  key={m.id}
                  data-testid={`admin-media-item-${m.id}`}
                  className="relative rounded-2xl border border-stone-200 bg-white overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
                >
                  {m.kind === "video" ? (
                    <div className="relative">
                      <video src={api.mediaFileUrl(m.id)} muted playsInline preload="metadata" className="w-full h-40 object-cover" />
                      <span className="absolute top-2 left-2 rounded-full bg-ink/70 px-2.5 py-1 flex items-center gap-1">
                        <Film className="w-3 h-3 text-cream" />
                        <span className="font-head text-[10px] font-bold text-cream">વિડિયો</span>
                      </span>
                    </div>
                  ) : (
                    <img src={api.mediaFileUrl(m.id)} alt={m.caption} loading="lazy" className="w-full h-40 object-cover" />
                  )}
                  <div className="p-3 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-head font-semibold text-xs text-ink truncate">{m.caption || "—"}</p>
                      <p className="font-head text-[10px] text-ink/45 mt-0.5">{fmtDate(m.created_at)}</p>
                    </div>
                    <button
                      data-testid={`admin-media-delete-${m.id}`}
                      onClick={() => deleteMedia(m.id)}
                      aria-label="કાઢી નાખો"
                      className="w-8 h-8 rounded-full bg-terracotta-soft text-terracotta flex items-center justify-center hover:bg-terracotta hover:text-cream transition-colors shrink-0"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
